import { z } from 'zod';
import { assetRefSchema, PRODUCT_STATUSES } from './catalog';
import { UUID_PATTERN, uuidSchema } from './common';
import { ALLOWED_IMAGE_CONTENT_TYPES, IMAGE_CONTENT_TYPE_EXTENSIONS } from './image-asset';
import { LANDING_MOVE_DIRECTIONS } from './landing';

// ---------------------------------------------------------------------------
// 기획전(exhibition_purposes·exhibitions·exhibition_products) 계약 — SUPER-61.
// 대표 이미지는 exhibitions/{uuid}.{ext}.
// 설계: docs/superpowers/specs/2026-09-23-exhibitions-design.md §6
// ---------------------------------------------------------------------------

export const EXHIBITION_PURPOSE_NAME_MAX_LENGTH = 50;
export const EXHIBITION_TITLE_MAX_LENGTH = 100;
export const EXHIBITION_LEAD_TIME_NOTE_MAX_LENGTH = 100;
/** 목적 탭·기획전 순서를 다시 매길 때의 간격. */
export const EXHIBITION_ORDER_STEP = 10;

/** 키 형식 전체를 고정해 `.`·`/` 로 다른 프리픽스에 닿는 경로를 막는다(landing 키 패턴과 같은 방식). */
export const EXHIBITION_IMAGE_KEY_PATTERN = new RegExp(
  `^exhibitions/${UUID_PATTERN}\\.(${Object.values(IMAGE_CONTENT_TYPE_EXTENSIONS).join('|')})$`,
);

export const exhibitionImageKeySchema = z
  .string()
  .regex(EXHIBITION_IMAGE_KEY_PATTERN, '기획전 이미지 키 형식이 아닙니다.');

/** 목적 탭 이름 — admin 폼과 서버가 같은 스키마로 검사한다. */
export const exhibitionPurposeNameSchema = z
  .string()
  .trim()
  .min(1, '목적 이름을 입력해 주세요.')
  .max(
    EXHIBITION_PURPOSE_NAME_MAX_LENGTH,
    `목적 이름은 ${EXHIBITION_PURPOSE_NAME_MAX_LENGTH}자 이하여야 합니다.`,
  );

/** 기획전 폼 필드 — admin 폼과 서버가 같은 스키마로 검사한다. */
export const exhibitionFieldsSchema = z.strictObject({
  purposeId: uuidSchema,
  title: z
    .string()
    .trim()
    .min(1, '기획전 이름을 입력해 주세요.')
    .max(
      EXHIBITION_TITLE_MAX_LENGTH,
      `기획전 이름은 ${EXHIBITION_TITLE_MAX_LENGTH}자 이하여야 합니다.`,
    ),
  leadTimeNote: z
    .string()
    .trim()
    .min(1, '납기 문구를 입력해 주세요.')
    .max(
      EXHIBITION_LEAD_TIME_NOTE_MAX_LENGTH,
      `납기 문구는 ${EXHIBITION_LEAD_TIME_NOTE_MAX_LENGTH}자 이하여야 합니다.`,
    ),
});
export type ExhibitionFields = z.infer<typeof exhibitionFieldsSchema>;

/** 공개 기획전 페이지 — 목적 탭과 카드를 한 번에 내려준다. 키는 싣지 않는다. */
export const publicExhibitionPurposeListSchema = z.object({
  purposes: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      exhibitions: z.array(
        z.object({
          id: z.uuid(),
          title: z.string(),
          imageUrl: z.url(),
          minOrderQty: z.number().int().positive(),
          /** 묶인 공개 상품 중 가장 싼 시작 단가(원) — 카드의 "N원부터". */
          baseUnitPrice: z.number().int().nonnegative(),
          leadTimeNote: z.string(),
        }),
      ),
    }),
  ),
});
export type PublicExhibitionPurposeList = z.infer<typeof publicExhibitionPurposeListSchema>;

export const publicExhibitionSummarySchema = z.object({ id: z.uuid(), title: z.string() });
export type PublicExhibitionSummary = z.infer<typeof publicExhibitionSummarySchema>;

export const createExhibitionPurposeInputSchema = z.strictObject({
  name: exhibitionPurposeNameSchema,
});
export type CreateExhibitionPurposeInput = z.infer<typeof createExhibitionPurposeInputSchema>;

export const renameExhibitionPurposeInputSchema = z.strictObject({
  id: uuidSchema,
  name: exhibitionPurposeNameSchema,
});
export type RenameExhibitionPurposeInput = z.infer<typeof renameExhibitionPurposeInputSchema>;

export const reorderExhibitionPurposesInputSchema = z.strictObject({
  orderedIds: z.array(uuidSchema).min(1),
});
export type ReorderExhibitionPurposesInput = z.infer<typeof reorderExhibitionPurposesInputSchema>;

export const deleteExhibitionPurposeInputSchema = z.strictObject({ id: uuidSchema });
export type DeleteExhibitionPurposeInput = z.infer<typeof deleteExhibitionPurposeInputSchema>;

export const listExhibitionsAdminInputSchema = z.strictObject({ purposeId: uuidSchema });
export type ListExhibitionsAdminInput = z.infer<typeof listExhibitionsAdminInputSchema>;

export const presignExhibitionImageUploadInputSchema = z.strictObject({
  contentType: z.enum(ALLOWED_IMAGE_CONTENT_TYPES),
});
export type PresignExhibitionImageUploadInput = z.infer<
  typeof presignExhibitionImageUploadInputSchema
>;

export const presignExhibitionImageUploadResultSchema = z.strictObject({
  uploadUrl: z.url(),
  key: exhibitionImageKeySchema,
  expiresInSeconds: z.number().int().positive(),
});
export type PresignExhibitionImageUploadResult = z.infer<
  typeof presignExhibitionImageUploadResultSchema
>;

export const createExhibitionInputSchema = z.strictObject({
  ...exhibitionFieldsSchema.shape,
  imageKey: exhibitionImageKeySchema.optional(),
});
export type CreateExhibitionInput = z.infer<typeof createExhibitionInputSchema>;

export const updateExhibitionInputSchema = z.strictObject({
  id: uuidSchema,
  ...exhibitionFieldsSchema.shape,
});
export type UpdateExhibitionInput = z.infer<typeof updateExhibitionInputSchema>;

export const replaceExhibitionImageInputSchema = z.strictObject({
  id: uuidSchema,
  imageKey: exhibitionImageKeySchema,
});
export type ReplaceExhibitionImageInput = z.infer<typeof replaceExhibitionImageInputSchema>;

export const setExhibitionVisibilityInputSchema = z.strictObject({
  id: uuidSchema,
  isVisible: z.boolean(),
});
export type SetExhibitionVisibilityInput = z.infer<typeof setExhibitionVisibilityInputSchema>;

export const moveExhibitionInputSchema = z.strictObject({
  id: uuidSchema,
  direction: z.enum(LANDING_MOVE_DIRECTIONS),
});
export type MoveExhibitionInput = z.infer<typeof moveExhibitionInputSchema>;

export const deleteExhibitionInputSchema = z.strictObject({ id: uuidSchema });
export type DeleteExhibitionInput = z.infer<typeof deleteExhibitionInputSchema>;

export const listExhibitionProductsInputSchema = z.strictObject({ exhibitionId: uuidSchema });
export type ListExhibitionProductsInput = z.infer<typeof listExhibitionProductsInputSchema>;

export const addExhibitionProductInputSchema = z.strictObject({
  exhibitionId: uuidSchema,
  productId: uuidSchema,
});
export type AddExhibitionProductInput = z.infer<typeof addExhibitionProductInputSchema>;

export const removeExhibitionProductInputSchema = z.strictObject({
  exhibitionId: uuidSchema,
  productId: uuidSchema,
});
export type RemoveExhibitionProductInput = z.infer<typeof removeExhibitionProductInputSchema>;

export const adminExhibitionPurposeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  displayOrder: z.number().int(),
});
export type AdminExhibitionPurpose = z.infer<typeof adminExhibitionPurposeSchema>;
export const adminExhibitionPurposeListSchema = z.array(adminExhibitionPurposeSchema);

const adminExhibitionBaseSchema = z.object({
  id: z.uuid(),
  purposeId: z.uuid(),
  title: z.string(),
  leadTimeNote: z.string(),
  displayOrder: z.number().int(),
  /** 묶인 공개 상품의 개수와 그중 최소 주문 수량. 공개 상품이 하나도 없으면 null. */
  publicProducts: z
    .object({
      count: z.number().int().positive(),
      minOrderQty: z.number().int().positive(),
    })
    .nullable(),
});

/** 공개 기획전은 대표 이미지가 있다(chk_exhibitions_visible_requires_image). 숨김만 키·URL 이 빌 수 있다. */
export const adminExhibitionSchema = z.discriminatedUnion('isVisible', [
  adminExhibitionBaseSchema.extend({
    isVisible: z.literal(true),
    imageKey: exhibitionImageKeySchema,
    imageUrl: z.url(),
  }),
  adminExhibitionBaseSchema.extend({
    isVisible: z.literal(false),
    imageKey: exhibitionImageKeySchema.nullable(),
    imageUrl: z.url().nullable(),
  }),
]);
export type AdminExhibition = z.infer<typeof adminExhibitionSchema>;
export const adminExhibitionListSchema = z.array(adminExhibitionSchema);

export const adminExhibitionProductSchema = z.object({
  productId: z.uuid(),
  name: z.string(),
  thumbnail: assetRefSchema.nullable(),
  status: z.enum(PRODUCT_STATUSES),
  isDeleted: z.boolean(),
});
export type AdminExhibitionProduct = z.infer<typeof adminExhibitionProductSchema>;
export const adminExhibitionProductListSchema = z.array(adminExhibitionProductSchema);
